import { isIntervalISODate } from '@epicuro-next/utilities';
import { BasketAccountAddress } from 'graphql-api/baskets-api-interfaces';
import { OrderType, ProductPriceRanges } from 'graphql-api/gql-api-interfaces';

import {
  DeliveryPeriodType,
  DisplayByMode,
  PriceType,
  ProductPricesInputModel,
  UsedInventoriesForDeliveryShop,
} from '../models';

export function getFirstInRange(
  prices: ProductPriceRanges[],
  deliveryPeriod: string | null,
) {
  if (deliveryPeriod && deliveryPeriod !== OrderType.Now) {
    return prices?.find(({ validityPeriod }) => {
      if (!validityPeriod) {
        return true;
      }
      const isEndAfterDelivery =
        !validityPeriod.endDate ||
        validityPeriod.endDate.localeCompare(deliveryPeriod) >= 0;
      const isStartBeforeDelivery =
        !validityPeriod.startDate ||
        validityPeriod.startDate.localeCompare(deliveryPeriod) <= 0;

      return isEndAfterDelivery && isStartBeforeDelivery;
    });
  }
  return prices ? prices[0] : null;
}

export function getFirstInFutureWhenNoDeliveryDate(
  prices?: ProductPriceRanges[],
) {
  if (!prices) {
    return null;
  }
  let firstInFuture = prices[0];
  const nowIso = new Date().toISOString();

  for (let idx = 1, len = prices.length; idx < len; idx += 1) {
    const curr = prices[idx];
    const currStartDate = curr.validityPeriod?.startDate;

    const isStartInFuture = currStartDate.localeCompare(nowIso) >= 0;
    const isEarlierThenPrevInFuture =
      firstInFuture.validityPeriod?.startDate.localeCompare(nowIso) >= 0
        ? currStartDate.localeCompare(
            firstInFuture.validityPeriod?.startDate,
          ) <= 0
        : currStartDate;

    if (isStartInFuture && isEarlierThenPrevInFuture) {
      firstInFuture = curr;
    }
  }

  return firstInFuture;
}

export function getPriceType(orderType: OrderType | null) {
  if (orderType === OrderType.Future) {
    return PriceType.Future;
  }

  return PriceType.Now;
}

export function getProductPrices(priceInputModel: ProductPricesInputModel) {
  let priceRange = null;

  if (!priceInputModel.productPrices) {
    return {};
  }

  const getPriceFn =
    !priceInputModel.deliveryPeriod &&
    priceInputModel.preSelectedOrderType === OrderType.Future
      ? getFirstInFutureWhenNoDeliveryDate
      : getFirstInRange;

  if (
    priceInputModel.prepackId &&
    (priceInputModel.displayByMode === DisplayByMode.Prepack ||
      priceInputModel.displayByMode === DisplayByMode.AdvancedPrepack) &&
    priceInputModel.showPrepackPrices
  ) {
    priceRange = getPriceFn(
      priceInputModel.productPrices[priceInputModel.prepackId],
      priceInputModel.deliveryPeriod,
    );
  }
  if (!priceRange && priceInputModel.sizeId) {
    priceRange = getPriceFn(
      priceInputModel.productPrices[priceInputModel.sizeId],
      priceInputModel.deliveryPeriod,
    );
  }
  if (!priceRange && priceInputModel.variantId) {
    priceRange = getPriceFn(
      priceInputModel.productPrices[priceInputModel.variantId],
      priceInputModel.deliveryPeriod,
    );
  }
  if (
    !priceRange &&
    priceInputModel.articleId &&
    priceInputModel.productPrices[priceInputModel.articleId]
  ) {
    priceRange = getPriceFn(
      priceInputModel.productPrices[priceInputModel.articleId],
      priceInputModel.deliveryPeriod,
    );
  }

  return priceRange ?? {};
}

export function getInventoriesFilteredByShopPeriod(
  usedInventories: UsedInventoriesForDeliveryShop,
  exclusions: {
    deliveryShopId: string;
    deliveryPeriod?: string;
  } = {
    deliveryShopId: '',
    deliveryPeriod: '',
  },
) {
  const otherPeriodsUsedInventories: {
    [invId: string]: number;
  } = {};

  Object.keys(usedInventories).forEach((deliveryShopId) => {
    Object.keys(usedInventories[deliveryShopId]).forEach((period) => {
      if (
        deliveryShopId === exclusions.deliveryShopId &&
        period === exclusions.deliveryPeriod
      ) {
        return;
      }
      const usedInventoriesInPeriod = usedInventories[deliveryShopId][period];
      Object.keys(usedInventoriesInPeriod).forEach((invId) => {
        otherPeriodsUsedInventories[invId] =
          (otherPeriodsUsedInventories[invId] ?? 0) +
          usedInventoriesInPeriod[invId];
      });
    });
  });
  return otherPeriodsUsedInventories;
}

// DO NOT USE IT!
// Use `getDeliveryShopId` in `delivery-shop-id.ts` file.
export function getDeliveryShopKey(deliveryShop: BasketAccountAddress) {
  return deliveryShop?.accountId + '-' + deliveryShop?.addressId;
}

export function getDeliveryTypeByDate(date: string) {
  if (date === DeliveryPeriodType.Now) {
    return DeliveryPeriodType.Now;
  }
  return isIntervalISODate(date)
    ? DeliveryPeriodType.Bulk
    : DeliveryPeriodType.Future;
}

export function mapDeliveryTypeToOrderType(
  deliveryType: DeliveryPeriodType,
): OrderType {
  if (deliveryType === DeliveryPeriodType.Now) {
    return OrderType.Now;
  }
  return OrderType.Future;
}

export function formatDisplayByMode(
  displayByMode: DisplayByMode | null,
  productIsLocked: boolean | null | undefined,
) {
  if (productIsLocked) {
    return DisplayByMode.Prepack;
  }
  return displayByMode ?? DisplayByMode.Size;
}
